import { useMemo } from 'react';
import useSWR from 'swr';
import type { SWRConfiguration } from 'swr';

import { fetcher, endpoints } from '@/lib/axios';
import type { IFaq, IFaqsResponse } from '@/types/faq';

// ----------------------------------------------------------------------

const swrOptions: SWRConfiguration = {
  revalidateIfStale: false,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  shouldRetryOnError: false,
};

// ----------------------------------------------------------------------

/**
 * Fetches FAQ questions for the given page/category slug.
 * Endpoint: GET /api/v1/faqs[?slug=slug]
 */
export function useGetFaqs(slug?: string) {
  const { data, isLoading, error, isValidating } = useSWR<IFaqsResponse>(
    endpoints.faqs.list(slug),
    fetcher,
    swrOptions
  );

  const memoizedValue = useMemo(
    () => ({
      faqs: (data?.data ?? []) as IFaq[],
      faqsLoading: isLoading,
      faqsError: error as Error | undefined,
      faqsValidating: isValidating,
      faqsEmpty: !isLoading && !(data?.data?.length),
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}
